import { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { CheckCircle2, Clock, EyeOff, Eye, X, Check } from "lucide-react";

function PricingCell({ therapy, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(therapy.display_monthly_price ?? "");
  const [promo, setPromo] = useState(therapy.promo_price ?? "");
  const [hide, setHide] = useState(!!therapy.hide_pricing);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        display_monthly_price: price === "" ? null : Number(price),
        promo_price: promo === "" ? null : Number(promo),
        hide_pricing: hide,
      };
      await api.patch(`/admin/therapies/${therapy.slug}/pricing`, payload);
      toast.success("Pricing updated");
      setEditing(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update pricing");
    } finally { setSaving(false); }
  };

  if (!editing) {
    const showPromo = Boolean(therapy.promo_price) && therapy.promo_price < therapy.display_monthly_price;
    return (
      <button
        data-testid={`pricing-cell-${therapy.slug}`}
        onClick={() => setEditing(true)}
        className="text-left hover:bg-stone-100 rounded px-2 py-1 transition-colors"
      >
        {therapy.hide_pricing ? (
          <span className="text-xs text-stone-400">Hidden</span>
        ) : therapy.display_monthly_price != null ? (
          <div>
            {showPromo ? (
              <div className="flex items-baseline gap-1">
                <span className="text-xs text-stone-400 line-through">${therapy.display_monthly_price}</span>
                <span className="text-sm font-semibold text-amber-700">${therapy.promo_price}</span>
              </div>
            ) : (
              <span className="text-sm font-semibold text-stone-900">${therapy.display_monthly_price}</span>
            )}
            <div className="text-[10px] uppercase tracking-widest text-stone-400">/mo</div>
          </div>
        ) : (
          <span className="text-xs text-stone-400 italic">Set price</span>
        )}
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1 min-w-[160px]" data-testid={`pricing-edit-${therapy.slug}`}>
      <div className="flex items-center gap-1">
        <span className="text-xs text-stone-500">$</span>
        <input
          data-testid={`pricing-input-${therapy.slug}`}
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Price"
          className="w-20 text-sm border border-stone-200 rounded px-2 py-1"
        />
        <span className="text-xs text-stone-400">/mo</span>
      </div>
      <div className="flex items-center gap-1">
        <input
          data-testid={`promo-input-${therapy.slug}`}
          type="number"
          value={promo}
          onChange={(e) => setPromo(e.target.value)}
          placeholder="Promo"
          className="w-20 text-xs border border-stone-200 rounded px-2 py-1"
        />
        <label className="text-[10px] flex items-center gap-1">
          <input type="checkbox" data-testid={`hide-pricing-${therapy.slug}`} checked={hide} onChange={(e) => setHide(e.target.checked)} /> hide
        </label>
      </div>
      <div className="flex gap-1">
        <button data-testid={`pricing-save-${therapy.slug}`} onClick={save} disabled={saving} className="text-xs px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700"><Check size={12} /></button>
        <button onClick={() => setEditing(false)} className="text-xs px-2 py-0.5 rounded bg-stone-100 hover:bg-stone-200"><X size={12} /></button>
      </div>
    </div>
  );
}

export function TherapiesTable({ therapies, onApprove, onToggleVis, onRefresh }) {
  return (
    <div className="vv-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 border-b border-stone-200">
          <tr>
            {["Therapy", "Regulatory", "Evidence", "Price", "Medical", "Compliance", "Public", "Actions"].map((h) => (
              <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {therapies.map((t) => (
            <tr key={t.slug} data-testid={`admin-row-${t.slug}`} className="border-b border-stone-100 hover:bg-stone-50">
              <td className="px-4 py-3">
                <div className="font-medium">{t.public_name}</div>
                <div className="text-xs text-stone-500">{t.slug}</div>
              </td>
              <td className="px-4 py-3 text-xs capitalize">{t.regulatory_status.replace(/_/g, " ")}</td>
              <td className="px-4 py-3 text-xs capitalize">{t.evidence_level}</td>
              <td className="px-4 py-3"><PricingCell therapy={t} onUpdate={onRefresh} /></td>
              <td className="px-4 py-3">{t.medical_approval_status ? <CheckCircle2 size={18} className="text-green-600" /> : <Clock size={18} className="text-stone-400" />}</td>
              <td className="px-4 py-3">{t.compliance_approval_status ? <CheckCircle2 size={18} className="text-green-600" /> : <Clock size={18} className="text-stone-400" />}</td>
              <td className="px-4 py-3">{t.public_visibility ? <span className="text-green-700 text-xs font-semibold">LIVE</span> : <span className="text-stone-500 text-xs">Draft</span>}</td>
              <td className="px-4 py-3">
                <div className="flex gap-1">
                  {!t.medical_approval_status && (
                    <button data-testid={`approve-medical-${t.slug}`} onClick={() => onApprove(t.slug, "medical")} className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100">Approve medical</button>
                  )}
                  {!t.compliance_approval_status && (
                    <button data-testid={`approve-compliance-${t.slug}`} onClick={() => onApprove(t.slug, "compliance")} className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200 hover:bg-indigo-100">Approve compliance</button>
                  )}
                  <button data-testid={`toggle-vis-${t.slug}`} onClick={() => onToggleVis(t.slug)} className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-800 border border-stone-200 hover:bg-stone-200 inline-flex items-center gap-1">
                    {t.public_visibility ? <><EyeOff size={12} /> Unpublish</> : <><Eye size={12} /> Publish</>}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ConsultationsTable({ consults }) {
  return (
    <div className="vv-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 border-b border-stone-200">
          <tr>{["Name", "Email", "State", "Goal", "Preferred time", "Created"].map((h) => (<th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">{h}</th>))}</tr>
        </thead>
        <tbody>
          {consults.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-500">No consultation requests yet.</td></tr>
          ) : consults.map((c, i) => (
            <tr key={`${c.id}-${i}`} className="border-b border-stone-100">
              <td className="px-4 py-3 font-medium">{c.name}</td>
              <td className="px-4 py-3">{c.email}</td>
              <td className="px-4 py-3">{c.state}</td>
              <td className="px-4 py-3">{c.goal}</td>
              <td className="px-4 py-3">{c.preferred_time}</td>
              <td className="px-4 py-3 text-xs text-stone-500">{c.created_at?.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
